import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { ReactNode, useId, useState } from "react";
import CustomBadge from "../Badge/CustomBadge";
import ClampedText from "./ClampedText";

export type ReportHeaderFact = {
	key: string;
	label: string;
	value: ReactNode;
};

export type ReportHeaderSummary = {
	key: string;
	label: string;
	text?: string;
};

type ReportHeaderProps = {
	// Names the collapsible details for their collapse button, such as
	// "Audit details".
	label: string;
	reportNumber?: string;
	// Internal or Global, for report types that have a scope; without it, no
	// scope is shown.
	isInternal?: boolean;
	title?: string;
	status: { text: string; color: string };
	// The buttons on the right of the top row, such as Edit and Export.
	actions?: ReactNode;
	// The line under the title: when and by whom the report was created.
	created?: ReactNode;
	// A line under that: what the report relates to, such as a lesson's
	// incident.
	related?: ReactNode;
	facts: ReportHeaderFact[];
	summaries?: ReportHeaderSummary[];
	// Shown beside the summaries, such as the report's asset card.
	media?: ReactNode;
	// Further details after the summaries, such as an incident's consequences
	// and potential risks.
	sections?: ReactNode;
	// The page's main content, such as the audit's findings: shown under the
	// details in the same card, and kept when they are collapsed.
	children?: ReactNode;
};

// One of a report's summaries: its label, then its text cut to a few lines,
// or emptyText (a dash unless given) when it has none. Only text is shown:
// nothing checks what else could be stored there.
export function ReportSummary({
	label,
	text,
	emptyText = "—",
}: Readonly<{ label: ReactNode; text?: unknown; emptyText?: string }>) {
	return (
		<div className="report-header__summary">
			<span className="report-header__label">{label}</span>
			{typeof text === "string" && text !== "" ? (
				<ClampedText
					text={text}
					className="report-header__text"
					buttonClassName="report-header__link"
				/>
			) : (
				<p className="report-header__muted">{emptyText}</p>
			)}
		</div>
	);
}

// The card at the top of a report's page: its number, scope (where the report
// type has one), status and title, the actions, and details that can be
// collapsed: when it was created and what it relates to, a strip of facts and
// its summaries, with any media beside them, then any further sections. The
// page's main content can follow in the same card.
function ReportHeader(props: Readonly<ReportHeaderProps>) {
	const {
		label,
		reportNumber,
		isInternal,
		title,
		status,
		actions,
		created,
		related,
		facts,
		summaries = [],
		media,
		sections,
		children,
	} = props;
	const [isExpanded, setIsExpanded] = useState(true);
	const bodyId = useId();
	const titleId = useId();
	const displayTitle = title || "—"; // NOSONAR: an empty title also shows the dash

	const summaryBlocks = summaries.map((summary) => (
		<ReportSummary
			key={summary.key}
			label={summary.label}
			text={summary.text}
		/>
	));

	// Named by its title, as it holds more than the details.
	return (
		<section className="report-header" aria-labelledby={titleId}>
			<div className="report-header__main">
				<div className="report-header__top">
					<div className="report-header__identity">
						{reportNumber && (
							<span className="report-header__number">{reportNumber}</span>
						)}
						{isInternal !== undefined && (
							<span className="report-header__scope">
								{isInternal ? "Internal" : "Global"}
							</span>
						)}
						<CustomBadge color={status.color}>{status.text}</CustomBadge>
					</div>
					<div className="report-header__actions">
						{actions}
						<Button
							icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
							aria-label={`${isExpanded ? "Hide" : "Show"} ${label}`}
							aria-expanded={isExpanded}
							aria-controls={isExpanded ? bodyId : undefined}
							onClick={() => setIsExpanded((value) => !value)}
						/>
					</div>
				</div>
				<h2 id={titleId} className="report-header__title">
					{displayTitle}
				</h2>
				{/* Removed rather than hidden, so a summary measures itself afresh
				    each time it is shown. */}
				{isExpanded && (
					<div id={bodyId} className="report-header__body">
						{created && <div className="report-header__created">{created}</div>}
						{related && <div className="report-header__related">{related}</div>}
						<dl className="report-header__facts">
							{facts.map((fact) => (
								<div key={fact.key}>
									<dt>{fact.label}</dt>
									<dd>{fact.value}</dd>
								</div>
							))}
						</dl>
						{media ? (
							<div className="report-header__media-row">
								<div className="report-header__media">{media}</div>
								<div className="report-header__summaries">{summaryBlocks}</div>
							</div>
						) : (
							summaryBlocks
						)}
						{sections}
					</div>
				)}
			</div>
			{children && <div className="report-header__content">{children}</div>}
		</section>
	);
}

export default ReportHeader;
